import { Badge, Button, ButtonLink, Card, Text } from "@backstage/ui";
import type { BundleHold } from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useCallback, useId, useState } from "react";

import { usePlatformAdmin } from "../identity/ownership";
import { PageHero, PageSection, PageShell } from "../layout/pageShell";

import { MY_AVD_HERO, MY_AVD_REMOVE_PATH, MY_AVD_SETUP_PATH } from "./avdEntitlement";
import { MyBundleHold } from "./bundleHold";
import { desiredIacCommitUrl } from "./desktopFacts";
import {
	type AvdActionState,
	avdActionCards,
	deleteOnFor,
	deleteOnText,
	heroSentence,
	MyAvdStatus,
	powerLine,
	useMyAvdDesktop,
	useMyAvdPower,
} from "./myDesktop";
import { reclaimText, useMyReclamation } from "./reclamation";

// AVD web client — signing in shows the desktops assigned to your account. It is itself stateful:
// if you already have a desktop it appears here; if not, the list is empty → set one up.
const AVD_WEB_CLIENT = "https://client.wvd.microsoft.com/arm/webclient/";

// Copy says "Set up" / "Remove", never "provision".
// Gating and layout: docs/app/avd-registry.md § How the page reads and gates on the registry.

const useStyles = makeStyles((theme) => ({
	actions: {
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
		gap: theme.spacing(1.75),
		"@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
	},
	card: { display: "flex", flexDirection: "column", gap: theme.spacing(1), height: "100%" },
	action: { marginTop: "auto", display: "grid", gap: theme.spacing(0.5), justifyItems: "start" },
	gated: { opacity: 0.6 },
	// BUI styles disabled from `data-disabled`, which `aria-disabled` never sets.
	inert: {
		cursor: "default",
		color: "var(--bui-fg-disabled)",
		background: "var(--bui-bg-neutral-1)",
		border: "1px solid var(--bui-border-1)",
		"&[data-hovered]": { background: "var(--bui-bg-neutral-1)" },
	},
	facts: { margin: 0, paddingLeft: "1.25rem", display: "grid", gap: theme.spacing(0.75) },
	explainer: { display: "grid", gap: theme.spacing(1.5) },
}));

/** A live link, or the reason above a focusable `aria-disabled` button that points at it. */
function AvdCardAction(
	props: Readonly<{
		action: AvdActionState;
		label: string;
		variant: "primary" | "secondary";
		href: string;
		external?: boolean;
		classes: ReturnType<typeof useStyles>;
	}>,
): ReactNode {
	const { action, label, variant, href, external, classes } = props;
	const reasonId = useId();

	if (!action.enabled) {
		return (
			<>
				<Text as="p" variant="body-small" color="secondary" id={reasonId}>
					{action.reason ?? "Not available right now."}
				</Text>
				<Button
					variant="secondary"
					className={classes.inert}
					aria-disabled="true"
					aria-describedby={reasonId}
				>
					{label}
				</Button>
			</>
		);
	}

	return (
		<ButtonLink
			variant={variant}
			href={href}
			{...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
		>
			{label}
		</ButtonLink>
	);
}

/** One lifecycle card: a title, what it does, and its action at the bottom. */
function AvdActionCard(
	props: Readonly<{
		title: string;
		description: string;
		action: AvdActionState;
		label: string;
		variant: "primary" | "secondary";
		href: string;
		external?: boolean;
		classes: ReturnType<typeof useStyles>;
	}>,
): ReactNode {
	const { title, description, action, classes } = props;
	return (
		<Card>
			<div className={classes.card}>
				<Text as="h3" variant="title-x-small">
					{title}
				</Text>
				<Text as="p" className={action.enabled ? undefined : classes.gated}>
					{description}
				</Text>
				<div className={classes.action}>
					<AvdCardAction {...props} />
				</div>
			</div>
		</Card>
	);
}

/** A colleague's home for their Azure Virtual Desktop: its state, then Connect / Set up / Remove. */
export const MyAvdPage = () => {
	const classes = useStyles();

	// One registry read serves the hero, the status card and the gating; `cards` fails open.
	const desktop = useMyAvdDesktop();
	const power = useMyAvdPower(desktop.view === "ready");
	const cards = avdActionCards(desktop.view, desktop.loading);
	const reclaimed = useMyReclamation(
		"avd",
		desktop.view === "absent" ? desktop.runUrl : undefined,
	);
	const reclaimedText = reclaimed ? reclaimText(reclaimed) : undefined;
	// The hold control's answer decides whether the reclaim date shows.
	const [hold, setHold] = useState<{ holdUntil?: string }>();
	const onHoldChange = useCallback(
		(next: BundleHold | undefined) => setHold({ holdUntil: next?.holdUntil }),
		[],
	);
	const deleteOn = hold
		? deleteOnFor({ ...desktop, status: desktop.view, holdUntil: hold.holdUntil }, new Date())
		: desktop.deleteOn;

	// Links into the private GHE repo are admin-only: docs/app/avd-registry.md § Developer-only links.
	const { admin } = usePlatformAdmin();

	const powerNode = desktop.view === "ready" ? powerLine(power) : null;
	const meta =
		powerNode || deleteOn ? (
			<>
				{powerNode}
				{deleteOn ? <Badge>Deletes on {deleteOnText(deleteOn)} unless used</Badge> : null}
			</>
		) : undefined;

	return (
		<PageShell>
			<PageHero
				eyebrow={MY_AVD_HERO.eyebrow}
				title={heroSentence(desktop.view, desktop.loading, reclaimedText)}
				meta={meta}
			/>

			<MyAvdStatus
				view={desktop.view}
				loading={desktop.loading}
				runUrl={admin ? desktop.runUrl : undefined}
				vmName={desktop.vmName}
				slug={desktop.slug}
				subscriptionName={desktop.subscriptionName}
				bundleType={desktop.bundleType}
				iacVersion={desktop.iacVersion}
				iacCommitUrl={admin ? desiredIacCommitUrl() : undefined}
				deleteOn={deleteOn}
				reclaimed={reclaimedText}
				hold={<MyBundleHold bundleType="avd" noun="desktop" onHoldChange={onHoldChange} />}
			/>

			<PageSection eyebrow="Your desktop" title="What you can do">
				<div className={classes.actions}>
					<AvdActionCard
						title="Connect"
						description="Open your desktop in the browser and sign in with your Implement account. If you already have a desktop, it appears here ready to use."
						action={cards.connect}
						label="Connect to my desktop"
						variant="primary"
						href={AVD_WEB_CLIENT}
						external
						classes={classes}
					/>
					<AvdActionCard
						title="Set up"
						description="First time here, or need to repair a desktop that's acting up? Setting one up takes about 10–15 minutes, and you'll get a notification when it's ready. Re-running this repairs your existing desktop — it won't wipe your files."
						action={cards.setup}
						label="Set up my desktop"
						variant="secondary"
						href={MY_AVD_SETUP_PATH}
						classes={classes}
					/>
					<AvdActionCard
						title="Remove"
						description="Done with your desktop? Remove it to stop its costs. This permanently deletes the machine and every file on it and cannot be undone — but you can always set up a fresh one later."
						action={cards.remove}
						label="Remove my desktop"
						variant="secondary"
						href={MY_AVD_REMOVE_PATH}
						classes={classes}
					/>
				</div>
			</PageSection>

			<PageSection eyebrow="Good to know" title="What is this, and what is it for?">
				<Card>
					<div className={classes.explainer}>
						<Text as="p">
							An <strong>AVD</strong> — Azure Virtual Desktop — is a personal Windows
							11 computer in Microsoft's cloud, reached through a browser and kept
							exactly as you left it between sessions. It's yours alone and
							disposable: somewhere to install a package, try a tool or run an
							experiment without touching your Implement laptop or network — remove it
							when you're done and set up a fresh one whenever you need one.
						</Text>
						<ul className={classes.facts}>
							<li>
								It's deliberately kept apart from Implement's internal systems, so
								it isn't the place for client-confidential material.
							</li>
							<li>
								It shuts down by itself at 22:00 each evening to keep the cost of an
								idle desktop near zero, and starts again the next time you connect.
								Saved files survive; unsaved work in an open app does not.
							</li>
							<li>
								The cost is billed to a project number you give when you set it up.
							</li>
						</ul>
					</div>
				</Card>
			</PageSection>
		</PageShell>
	);
};
